import type { CardDto } from '@kanban/shared';
import { useState } from 'react';
import { Avatar } from './Avatar.js';
import { DueChip } from './DueChip.js';
import { PriorityDot } from './PriorityDot.js';

/**
 * Kompakte Kartendarstellung im Board (SPEC 9.1): Titel auf zwei Zeilen,
 * Prioritaet, Faelligkeit, Verantwortliche. Mehr gehoert auf 375 px nicht
 * auf eine Karte.
 *
 * Bewusst KEIN <button> AUF DER GANZEN KACHEL: dnd-kit verweigert den
 * Drag-Start, wenn die Geste auf einem interaktiven Element beginnt (button,
 * input, select …) — die Karte waere dann nicht zu ziehen. Fokus, Rolle und
 * Tastaturbedienung liefert die umschliessende `SortableCard`.
 *
 * Der Erledigt-Knopf unten ist die einzige Ausnahme: er haelt sich per
 * `stopPropagation` selbst aus der Drag-Erkennung heraus, statt dass die
 * ganze Kachel interaktionsfrei bleiben muss.
 */
export function CardTile({
  card,
  onOpen,
  /** Wer diese Karte gerade offen hat (SPEC 7.3) — `null` = niemand sonst. */
  watcherName = null,
  /** Schnell erledigen, ohne das Kartendetail zu oeffnen. Weggelassen = kein Knopf. */
  onComplete,
}: {
  card: CardDto;
  onOpen: (card: CardDto) => void;
  watcherName?: string | null;
  onComplete?: (card: CardDto) => void;
}) {
  // Kurzer Puls am Knopf plus Ausblenden der Kachel, bevor der optimistische
  // Cache-Eintrag sie tatsaechlich in die Erledigt-Spalte setzt — sonst
  // verschwaende die Karte ohne jede Rueckmeldung.
  const [completing, setCompleting] = useState(false);

  function complete(event: React.PointerEvent | React.MouseEvent) {
    event.stopPropagation();
    if (!onComplete || completing) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      onComplete(card);
      return;
    }
    setCompleting(true);
    window.setTimeout(() => onComplete(card), 150);
  }

  return (
    <div
      onClick={() => onOpen(card)}
      data-testid="card-tile"
      data-card-id={card.id}
      // Der Hinweis ist ein Rahmen und kein eigenes Element: `CardTile` darf
      // sonst nichts Interaktives enthalten, siehe Kommentar oben.
      data-watched={watcherName ? 'ja' : undefined}
      title={watcherName ? `${watcherName} schaut sich diese Karte gerade an` : undefined}
      className={`relative w-full rounded-[var(--radius)] border bg-surface p-3 text-left shadow-[var(--shadow-1)] transition-all duration-150 hover:shadow-[var(--shadow-2)] ${
        watcherName ? 'border-accent' : 'border-border'
      } ${completing ? 'scale-95 opacity-0' : ''}`}
      style={{ minHeight: 'var(--touch-target)' }}
    >
      {/* Label-Farbstreifen (SPEC 9.1): erkennbar, ohne Platz fuer Namen zu
          verbrauchen — auf 375 px ist jede Zeile kostbar. */}
      {card.labels.length > 0 && (
        <div className="mb-2 flex gap-1" data-testid="label-stripes">
          {card.labels.map((label) => (
            <span
              key={label.id}
              title={label.name}
              aria-label={label.name}
              className="h-1.5 w-8 rounded-full"
              style={{ backgroundColor: `var(--color-label-${label.color})` }}
            />
          ))}
        </div>
      )}

      <div className="flex items-start gap-2 pr-8">
        <PriorityDot priority={card.priority} />
        <span
          className={`line-clamp-2 flex-1 text-sm font-medium ${card.completedAt ? 'text-muted line-through' : ''}`}
        >
          {card.title}
        </span>
      </div>

      {(card.dueAt ||
        card.assignees.length > 0 ||
        card.checklist.total > 0 ||
        card.commentCount > 0 ||
        card.attachmentCount > 0) && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <DueChip dueAt={card.dueAt} dueHasTime={card.dueHasTime} completedAt={card.completedAt} />
            {card.checklist.total > 0 && (
              <span
                data-testid="checklist-count"
                className={`text-xs ${
                  card.checklist.done === card.checklist.total ? 'text-success' : 'text-muted'
                }`}
                title={`${card.checklist.done} von ${card.checklist.total} erledigt`}
              >
                ☑ {card.checklist.done}/{card.checklist.total}
              </span>
            )}
            {/* Kommentar-Icon mit Anzahl (SPEC 9.1): zeigt, wo besprochen
                wird — sonst muesste man jede Karte einzeln oeffnen. */}
            {card.commentCount > 0 && (
              <span
                data-testid="comment-count"
                className="text-xs text-muted"
                title={`${card.commentCount} Kommentar${card.commentCount === 1 ? '' : 'e'}`}
              >
                💬 {card.commentCount}
              </span>
            )}
            {/* Bueroklammer mit Anzahl (SPEC 9.1, 9.8): dass ein Foto
                dranhaengt, ist oft der Grund, die Karte ueberhaupt zu oeffnen. */}
            {card.attachmentCount > 0 && (
              <span
                data-testid="attachment-count"
                className="text-xs text-muted"
                title={`${card.attachmentCount} ${card.attachmentCount === 1 ? 'Anhang' : 'Anhänge'}`}
              >
                📎 {card.attachmentCount}
              </span>
            )}
          </span>
          <span className="flex -space-x-1">
            {card.assignees.map((assignee) => (
              <Avatar key={assignee.id} name={assignee.name} color={assignee.avatarColor} />
            ))}
          </span>
        </div>
      )}

      {onComplete && !card.completedAt && (
        <button
          type="button"
          data-testid="complete-card"
          aria-label={`${card.title} als erledigt markieren`}
          title="Als erledigt markieren"
          onPointerDown={(event) => event.stopPropagation()}
          onClick={complete}
          className="absolute right-2 top-2 flex items-center justify-center rounded-full border border-border-strong bg-surface text-muted transition-transform duration-150 hover:border-success hover:text-success active:scale-125"
          style={{ minWidth: '2rem', minHeight: '2rem' }}
        >
          <span aria-hidden="true">✓</span>
        </button>
      )}
    </div>
  );
}
